import { withStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import { CircularProgress } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { FaCheckCircle, FaExclamationCircle, FaRegCheckCircle, FaRegCircle, FaRegTimesCircle, FaTrashAlt } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { submitAddProjectRequestWithDocs, submitAddProjectRequest, submitEditProjectRequest } from "../../../redux/actions/lenderRequestActions";
import { uploadTempFile, appendSuffix } from "../../shared/services/FileUploadService";
import { isOverflowingX } from "../../shared/utilities/isOverflowing";
import { DocumentUpload } from "../../LenderRequests/Shared/DocumentUpload";
import "./AttachmentsPage.css";

// TODO: Confirm this is correct
const techSupportUrl = "https://singlefamily.fanniemae.com/applications-technology/technology-support-center";

const sx = {
  tooltip: {
    color: "black",
    backgroundColor: "white",
    border: "1px solid #E6E8ED",
    fontSize: 14,
  },
  arrow: {
    color: "white",
    "&:before": {
      border: "1px solid #E6E8ED",
    },
  }
};

const StyledTooltip = withStyles(sx)(Tooltip);

// Show tooltip only if text is truncated
const CustomTooltip = (props) => {
  return <StyledTooltip
    {...props}
    disableFocusListener
    disableTouchListener
    disableHoverListener={props.target && !isOverflowingX(props.target)}
  />;
};

const AttachmentsPage = ({ tempId, incrementCounter, decrementCounter, onPartialFailure }) => {  
  const [showValidation, setShowValidation] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [projectCreatedButFilesFailed, setProjectCreatedButFilesFailed] = useState(false);
  const [fileStatuses, setFileStatuses] = useState(new Map());
  const [reconciliationOutcome, setReconciliationOutcome] = useState(null);
  
  const dispatch = useDispatch(); 
  const requirements = useSelector(store => store.lenderRequestReducer.requiredDocTypes);
  const optional = useSelector(store => store.lenderRequestReducer.optionalDocTypes);
  const projectInfo = useSelector(store => store.lenderRequestReducer.projectInfo);
  const requestTypeName = useSelector(store => store.lenderRequestReducer.requestTypeName);
  const requestId = useSelector(store => store.lenderRequestReducer.requestId);

  const [requiredDropdown, setRequiredDropdown] = useState([]);
  const [optionalDropdown, setOptionalDropdown] = useState([]);
  const [unfilledRequirements, setUnfilledRequirements] = useState();
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (optional && requirements && uploads) {
      if (!requirements.flat().length) {
        // should be impossible
        console.log('No required documents, skipping to next step');

        getOrCreateRequestId().then(() => 
          incrementCounter()
        );

        return;
      }

      const addedDocTypes = new Set(uploads.map(fileObj => fileObj?.docType?.documentRankingId));
      const remainingNested = requirements.map(reqSet => 
        reqSet.some(req => addedDocTypes.has(req.documentRankingId))
          ? [] 
          : reqSet
      );
      const fulfilledNested = requirements.map(reqSet => 
        reqSet.some(req => addedDocTypes.has(req.documentRankingId))
          ? reqSet
          : []
      );

      const remainingFlat = new Set(remainingNested.flat());
      const fulfilled = new Set(fulfilledNested.flat());
      const fulfilledOrOptional = new Set([ ...fulfilled, ...optional.flat() ]);

      // const subTypeLookup = [...requirements.flat(), ...optional.flat()].reduce((lookup, docTypeObj) => ({
      //   ...lookup,
      //   [docTypeObj.name]: docTypeObj,
      //   [docTypeObj.documentRankingId]: docTypeObj,
      // }), {});

      console.log('requirements updated', {requirements, optional, uploads, addedDocTypes, remainingNested, fulfilledNested, remainingFlat, fulfilledOrOptional});

      // setDocSubTypeLookup(subTypeLookup);      
      setRequiredDropdown([ ...remainingFlat ]);
      setOptionalDropdown([ ...fulfilledOrOptional ]);
      setUnfilledRequirements(remainingNested);
      setUploadFailed(false);
    }    
  }, [optional, requirements, uploads]);

  useEffect(() => {
    if (unfilledRequirements && requiredDropdown) {
      setIsValid(!requiredDropdown.length);
    }
  }, [unfilledRequirements, requiredDropdown])

  // Helper to update individual file status
  function setFileStatus(index, status, extra = {}) {
    setFileStatuses(prev => {
      const next = new Map(prev);
      next.set(index, { ...next.get(index), status, ...extra });
      return next;
    });
  }

  // Derived status flags
  const hasUploading = [...fileStatuses.values()].some(s => s.status === 'uploading');

  function handleAddFile(fileObj) {
    const files = [ ...uploads, fileObj ];
    setUploads(files);
  }

  /**
   * Upload a single file to temp storage and update its status.
   * Returns a promise that resolves on success or rejects on failure.
   */
  function uploadSingleFile(fileObj, index) {
    return new Promise((resolve, reject) => {
      setFileStatus(index, 'uploading', { error: null });

      // Guard against the underlying service calling both callbacks (e.g. SPE
      // calls onSuccess then CDX fallback calls onFail for the same file).
      let settled = false;
      // Reuse a previously computed storage name on retry; compute once otherwise.
      const storageFileName = fileObj.storageFileName || appendSuffix(fileObj.file.name);
      // Intentional mutation — storageFileName is a derived upload-time value, not tracked by React.
      fileObj.storageFileName = storageFileName;

      uploadTempFile(
        fileObj.file,
        tempId,
        storageFileName,
        (data) => {
          if (settled) return;
          settled = true;
          setFileStatus(index, 'completed', { uploadDetails: data });
          resolve(data);
        },
        (error) => {
          if (settled) return;
          settled = true;
          setFileStatus(index, 'failed', { error: error?.toString() });
          reject(error);
        }
      );
    });
  }

  function handleRemoveFile(index) {
    const files = [ ...uploads ];
    files.splice(index, 1);
    setUploads(files);

    // Re-key fileStatuses after removal
    setFileStatuses(prev => {
      const next = new Map();
      let newIdx = 0;
      for (let i = 0; i < prev.size + 1; i++) {
        if (i === index) continue;
        if (prev.has(i)) {
          next.set(newIdx, prev.get(i));
          newIdx++;
        }
      }
      return next;
    });

    setShowValidation(true);
  }

  function handleBackButton(e) {
    e.preventDefault();    
    decrementCounter();
  }

  async function getOrCreateRequestId() {    
    const userRole = sessionStorage.getItem('role');

    console.groupCollapsed('getOrCreateRequestId');
    console.log('userRole', userRole);
    console.log('requestTypeName', requestTypeName);
    console.log('requestId', requestId);
    console.log('projectInfo', projectInfo);
    console.groupEnd();

    if (!requestId) {
      try {
        // todo: convert to callback pattern
        const promise = requestTypeName === "Lender Add Project"
            ? dispatch(submitAddProjectRequest(projectInfo))
            : dispatch(submitEditProjectRequest(projectInfo.projectId));

        console.groupCollapsed('getOrCreateRequestId result');
        console.log('requestTypeName', requestTypeName);
        console.log('promise', promise);
        
        const {requestId, projectId, phaseId} = await promise;

        console.log('results', {requestId, projectId, phaseId});
        console.groupEnd();

        return {requestId, projectId, phaseId};
      } catch (error) {
        setUploadFailed(true); 
        setUploading(false);
        console.error("Error during getOrCreateRequestId", error);
        throw error;
      }
    }

    console.log('Using existing requestId', requestId);
    return requestId;
  }

  async function handleClose(e) {
    e.preventDefault();

    if (projectCreatedButFilesFailed) {
      // Project already exists — just navigate to the success page.
      incrementCounter();
      return;
    }

    // File uploads failed before the project was ever created.
    // Submit the project now without any documents so it is not lost.
    setUploading(true);
    try {
      const result = await dispatch(submitAddProjectRequestWithDocs(projectInfo, tempId, []));
      if (onPartialFailure && result?.partialFailure) onPartialFailure(true);
    } catch (error) {
      // Project may have been created despite the error (e.g. duplicate/partial failure).
      // Navigate forward regardless — the user confirmed the project exists in the DB.
      console.error('[handleClose] project creation without docs failed', error);
      if (onPartialFailure) onPartialFailure(true);
    } finally {
      setUploading(false);
      incrementCounter();
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    setUploadFailed(false);
    setShowValidation(true);

    if (!isValid || hasUploading) return;

    setUploading(true);

    try {
      // Upload files that haven't been uploaded yet (pending or previously failed)
      const uploadPromises = uploads.map((fileObj, index) => {
        const status = fileStatuses.get(index)?.status;
        if (status === 'completed') {
          return Promise.resolve(); // already uploaded
        }
        return uploadSingleFile(fileObj, index);
      });

      const results = await Promise.allSettled(uploadPromises);
      const anyFailed = results.some(r => r.status === 'rejected');

      if (anyFailed) {
        setUploadFailed(true);
        setUploading(false);
        return;
      }
    } catch (error) {
      // Error during file upload phase — project was never attempted
      setUploadFailed(true);
      setUploading(false);
      return;
    }

    // All files uploaded successfully. Now create the project and associate temp docs.
    // Any error from here on means files are in temp storage but project creation failed.
    try {
      const tempDocuments = uploads.map((fileObj) => ({
        documentRankingId: fileObj.docType.documentRankingId,
        fileName: fileObj.storageFileName || fileObj.file.name,
        note: fileObj.note || ""
      }));

      const result = await dispatch(
        submitAddProjectRequestWithDocs(projectInfo, tempId, tempDocuments)
      );

      if (result?.reconciliationOutcome) {
        const ro = result.reconciliationOutcome;
        console.log('[reconciliationOutcome_received]', ro);
        if (ro.resolutionState === 'successSubmitted') {
          incrementCounter();
        } else if (ro.resolutionState === 'successDraft') {
          setProjectCreatedButFilesFailed(true);
          setUploadFailed(true);
          if (onPartialFailure) onPartialFailure(true);
        } else {
          // ambiguousManualReview | unresolvedNotFound | reconciliationValidationError | reconciliationServerError
          setReconciliationOutcome(ro);
        }
        return;
      }

      if (result?.partialFailure === true) {
        console.warn('[partialFailure_detected]', { requestId: result?.requestId, projectId: result?.projectId });
        setProjectCreatedButFilesFailed(true);
        setUploadFailed(true); // Keep for backwards-compatible CSS/display logic
        if (onPartialFailure) onPartialFailure(true);
      } else {
        incrementCounter();
      }
    } catch (error) {
      const filesWereUploaded = [...fileStatuses.values()].every(s => s.status === 'completed');
      console.error('[submitAddProjectRequestWithDocs_failed]', error);

      if (filesWereUploaded) {
        setProjectCreatedButFilesFailed(true);
        if (onPartialFailure) onPartialFailure(true);
      }
      setUploadFailed(true);
    } finally {
      setUploading(false);
    }
  }

  function describeRequirement(reqSet) {
    // console.log('describeRequirement', reqSet)
    const labels = reqSet.map(req => req.name);

    return reqSet.length === 1
      ? labels[0]
      : reqSet.length === 2
      ? `${labels[0]} and/or ${labels[1]}`
      : `At least one of ${labels.slice(0, -1).join(", ")}, and/or ${labels[labels.length - 1]}`;
  }

  function getBullet(index) {
    // all missing required docTypes in this subset, if any
    const missingDocs = unfilledRequirements[index];

    return missingDocs && !missingDocs.length // requirement fulfilled
      ? <FaCheckCircle className="req-bullet completed" />
      : !showValidation || !missingDocs  // validation not triggered or list not initialized
      ? <FaRegCircle className="req-bullet default" />
      : <FaExclamationCircle className="req-bullet error" />
  }

  const fileNameRefs = useRef([]);
  const fileNoteRefs = useRef([]);
  const fileDocTypeRefs = useRef([]);

  return (
    <form className={"upload-docs-form " + (showValidation ? "show-validation" : "")}>
      <section className="title-section">
        <h1>{requestTypeName || "Lender"} Request</h1>
        <h6>Section 2 of 3 - Upload Documents</h6>
      </section>
    
      {unfilledRequirements ? (
        <section className="main-section">
          {requiredDropdown.length ? (
            <>           
              <p>Please upload the following documents:</p>
              <ul className="req-list">
                {requirements.map((reqSet, index) => 
                  <li key={index}>
                    {getBullet(index)}
                    <div className="req-desc">
                      {describeRequirement(reqSet)}
                    </div>
                  </li>
                )}
              </ul>
            </>
          ) : (
            <p>At least one supporting document must be included with this request.</p>
          )}
          {uploads.length ? ( 
            <div className="uploaded-files-list" aria-live="polite">
              <div className="uploaded-files-row header-row">
                <div className="file-doctype">Document Type</div>
                <div className="file-status-bar">File</div>             
                <div className="file-note">Note</div>
              </div>
              {uploads.map((fileObj, index) => (
                <div key={index} className="uploaded-files-row">
                  <CustomTooltip 
                    target={fileDocTypeRefs.current[index]} 
                    title={fileObj.docType.name} 
                    placement="top"
                  >
                    <div ref={ref => fileDocTypeRefs.current[index] = ref} className="file-doctype">
                      {fileObj.docType.name}
                    </div>
                  </CustomTooltip>

                  <div className="file-status-bar">
                    <CustomTooltip target={fileNameRefs.current[index]} title={fileObj.file.name} placement="top">
                      <div ref={ref => fileNameRefs.current[index] = ref} className="file-name">
                        {fileObj.file.name}
                      </div>
                    </CustomTooltip>

                    {/* Upload status indicator */}
                    {(uploadFailed || projectCreatedButFilesFailed) ? (
                      // Any failure state: all icons become red crosses regardless of individual file status
                      <FaRegTimesCircle
                        size={30}
                        className="file-status-icon error"
                        role="img"
                        aria-label={`${fileObj.file.name} upload failed`}
                        style={{ color: '#d32f2f', marginLeft: '8px' }}
                      />
                    ) : (
                      <>
                        {fileStatuses.get(index)?.status === 'uploading' && (
                          <CircularProgress size={18} style={{ color: '#1976d2', marginLeft: '8px' }} aria-busy="true" />
                        )}
                        {fileStatuses.get(index)?.status === 'completed' && (
                          <FaRegCheckCircle
                            size={16}
                            className="file-status-icon completed"
                            role="img"
                            aria-label={`${fileObj.file.name} upload completed`}
                            style={{ color: '#2e7d32', marginLeft: '8px' }}
                          />
                        )}
                        {fileStatuses.get(index)?.status === 'failed' && (
                          <FaRegTimesCircle
                            size={16}
                            className="file-status-icon error"
                            role="img"
                            aria-label={`${fileObj.file.name} upload failed`}
                          />
                        )}
                      </>
                    )}

                    {!uploadFailed && !projectCreatedButFilesFailed && (
                      <div className="file-remove" onClick={() => handleRemoveFile(index)}>
                        <FaTrashAlt className="delete-button" />
                      </div>
                    )}
                  </div>        

                  <CustomTooltip 
                    target={fileNoteRefs.current[index]} 
                    title={fileObj.note} 
                    placement="top"
                  >
                    <div ref={ref => fileNoteRefs.current[index] = ref} className="file-note">
                      {fileObj.note ? fileObj.note : (
                        <span className="empty-field">--</span>
                      )}
                    </div>
                  </CustomTooltip>
                </div>
              ))}
            </div>
          ) : (<></>)}
          <DocumentUpload
            requiredList={requiredDropdown} 
            optionalList={optionalDropdown} 
            onAddFile={fileObj => handleAddFile(fileObj)}
            disabled={uploadFailed || projectCreatedButFilesFailed}
            />
        </section>
      ) : (<></>)}

      <div className="stepper-controls" style={{marginTop: "40px"}}>
        <div className="control-cell">
          <Button
            type="submit"
            className="back-button"
            onClick={handleBackButton}
            disabled={uploadFailed || uploading || hasUploading}
            form="addProjectForm"
          >
            Back
          </Button>
        </div>
        <div className="control-cell">
          <Button
            type="submit"
            className={"btn-submit" + 
              (!isValid ? " btn-invalid" : "") +
              (uploading ? " btn-loading" : "")
            }
            disabled={uploading || hasUploading}
            onSubmit={uploadFailed || reconciliationOutcome ? handleClose : handleSubmit}
            onClick={uploadFailed || reconciliationOutcome ? handleClose : handleSubmit}
            form="addProjectForm"
          >
            {uploading ? (
              <CircularProgress
                style={{
                  display: "inline-flex",
                  padding: "10px",
                  color: "#fff",
                  justifySelf: "center",
                  alignSelf: "center"
                }}
              />
            ) : (uploadFailed || reconciliationOutcome) ? "Close" : "Submit Project"}              
          </Button>
        </div>
      </div>

      {(uploadFailed || projectCreatedButFilesFailed) ? (
        <div className="upload-error-message" role="alert" aria-live="polite">
          <div>
            {projectCreatedButFilesFailed
              ? <>Project created successfully, but file upload failed. No files were added. Please use the &ldquo;Add Document&rdquo; button to upload them.</>
              : <>File upload failed. Click &ldquo;Close&rdquo; to save your project without documents. You can upload them later using the &ldquo;Add Document&rdquo; button on the request page.</>
            }
          </div>
        </div>
      ) : (<></>)}

      {reconciliationOutcome && (
        <div className="upload-error-message" role="alert" aria-live="polite">
          <div>
            {reconciliationOutcome.resolutionState === 'ambiguousManualReview' && (
              <>We were unable to automatically confirm the status of your submission. Please check the&nbsp;
                <a href={techSupportUrl} target="_blank" rel="noreferrer">request feed</a>&nbsp;
                to verify whether your project was submitted successfully before trying again.
              </>
            )}
            {reconciliationOutcome.resolutionState === 'unresolvedNotFound' && (
              <>Your submission timed out and we could not locate the resulting project. Please check the&nbsp;
                <a href={techSupportUrl} target="_blank" rel="noreferrer">request feed</a>&nbsp;
                or contact support if the project does not appear.
              </>
            )}
            {(reconciliationOutcome.resolutionState === 'reconciliationValidationError' ||
              reconciliationOutcome.resolutionState === 'reconciliationServerError') && (
              <>An error occurred while checking your submission status. Please&nbsp;
                <a href={techSupportUrl} target="_blank" rel="noreferrer">contact support</a>&nbsp;
                to verify whether your project was submitted successfully.
              </>
            )}
          </div>
        </div>
      )}
    </form>
  );
};

export default AttachmentsPage;
